import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import { CorrectIcon, EyeIcon, EyeOffIcon, LinkCopyIcon } from '../../../../../ui/Icons';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  BankAccountFormUIModel,
  BankAccountUIModel
} from '../../../../../shared/ui-models/landlord.ui-model';
import { isBankAccountSaveError, saveBankAccount } from '../../../../data.service';

export interface BankAccountCardProps {
  config: {
    landlordId: string;
    bankAccount: BankAccountUIModel | null;
  };
  eventHandlers: {
    savedHandler: () => void;
  };
}

const FIELDS: {
  key: keyof BankAccountFormUIModel;
  label: string;
  optional?: boolean;
  placeholder?: string;
}[] = [
  { key: 'bankName', label: 'Bank name' },
  { key: 'accountHolderName', label: 'Account holder name' },
  { key: 'accountNumber', label: 'Account number' },
  { key: 'swiftBic', label: 'SWIFT / BIC', optional: true },
  { key: 'country', label: 'Country', optional: true, placeholder: 'EG' },
  { key: 'currency', label: 'Currency', optional: true, placeholder: 'EGP' }
];

const EMPTY_FORM: BankAccountFormUIModel = {
  bankName: '',
  accountHolderName: '',
  accountNumber: '',
  swiftBic: '',
  country: '',
  currency: ''
};

function toForm(bankAccount: BankAccountUIModel | null): BankAccountFormUIModel {
  if (!bankAccount) {
    return { ...EMPTY_FORM };
  }
  return {
    bankName: bankAccount.bankName,
    accountHolderName: bankAccount.accountHolderName,
    accountNumber: bankAccount.accountNumber,
    swiftBic: bankAccount.swiftBic,
    country: bankAccount.country,
    currency: bankAccount.currency
  };
}

function formatTimestamp(value: string): string {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
}

function maskAccountNumber(value: string): string {
  if (!value) {
    return '';
  }
  const last4 = value.slice(-4);
  const masked = '•'.repeat(Math.max(value.length - 4, 0)) + last4;
  return (masked.match(/.{1,4}/g) || [masked]).join(' ');
}

const inputClass = (hasError: boolean) =>
  `w-full rounded-lg border px-3 py-2 text-sm text-neutral-500 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 ${
    hasError ? 'border-scarlet focus:border-scarlet' : 'border-gray-200 focus:border-primary-500'
  }`;

export const BankAccountCard: React.FC<BankAccountCardProps> = ({ config, eventHandlers }) => {
  const { bankAccount, landlordId } = config;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [form, setForm] = useState<BankAccountFormUIModel>(toForm(bankAccount));
  const [fieldErrors, setFieldErrors] = useState<{ [field: string]: string }>({});
  const [formError, setFormError] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isAccountRevealed, setIsAccountRevealed] = useState<boolean>(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const copyTimeoutRef = useRef<number>();

  useEffect(() => {
    setIsAccountRevealed(false);
  }, [bankAccount?.accountNumber]);

  useEffect(() => () => window.clearTimeout(copyTimeoutRef.current), []);

  const startEditing = useCallback(() => {
    setForm(toForm(bankAccount));
    setFieldErrors({});
    setFormError('');
    setIsEditing(true);
  }, [bankAccount]);

  const handleChange = useCallback((field: keyof BankAccountFormUIModel, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => {
      if (!current[field]) {
        return current;
      }
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  const copyValue = useCallback((field: string, value: string) => {
    if (!value || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    window.clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = window.setTimeout(() => setCopiedField(null), 1500);
  }, []);

  const handleSave = useCallback(() => {
    setIsSaving(true);
    setFormError('');
    setFieldErrors({});

    saveBankAccount(landlordId, form).then((response) => {
      if (response === true) {
        setIsEditing(false);
        eventHandlers.savedHandler();
      } else if (isBankAccountSaveError(response)) {
        const mapped: { [field: string]: string } = {};
        response.fieldErrors.forEach((error) => {
          mapped[error.field] = error.message;
        });
        setFieldErrors(mapped);
        setFormError(response.message);
      } else if (isErrorInfo(response)) {
        setFormError(response.errorMessage || 'The bank account could not be saved');
      }
      setIsSaving(false);
    });
  }, [landlordId, form, eventHandlers]);

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Bank account</h2>
        {!isEditing && (
          <Button type={ButtonType.default} size={ButtonSize.small} onClick={startEditing}>
            {bankAccount ? 'Edit' : 'Add bank account'}
          </Button>
        )}
      </div>

      {!isEditing && !bankAccount && (
        <p className="text-sm text-gray-500">No bank account on file</p>
      )}

      {!isEditing && bankAccount && (
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Bank name</div>
              <div className="text-sm font-medium text-gray-800">{bankAccount.bankName || '-'}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Account holder</div>
              <div className="text-sm font-medium text-gray-800">
                {bankAccount.accountHolderName || '-'}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-primary-100 bg-primary-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-primary-700">
                  Account number
                </div>
                <div className="mt-1 truncate font-mono text-sm tracking-wider text-neutral-500">
                  {bankAccount.accountNumber
                    ? isAccountRevealed
                      ? bankAccount.accountNumber
                      : maskAccountNumber(bankAccount.accountNumber)
                    : '-'}
                </div>
              </div>
              {bankAccount.accountNumber && (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="rounded-lg p-1.5 text-primary-700 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-300"
                    aria-label={isAccountRevealed ? 'Hide account number' : 'Show account number'}
                    aria-pressed={isAccountRevealed}
                    onClick={() => setIsAccountRevealed((current) => !current)}
                  >
                    {isAccountRevealed ? <EyeOffIcon size="18px" /> : <EyeIcon size="18px" />}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-1.5 text-primary-700 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-300"
                    aria-label="Copy account number"
                    onClick={() => copyValue('accountNumber', bankAccount.accountNumber)}
                  >
                    {copiedField === 'accountNumber' ? (
                      <CorrectIcon size="18px" />
                    ) : (
                      <LinkCopyIcon size="18px" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {bankAccount.swiftBic && (
              <div className="mt-3 flex items-center justify-between gap-3 border-t border-primary-100 pt-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wide text-primary-700">
                    SWIFT / BIC
                  </div>
                  <div className="mt-1 truncate font-mono text-sm tracking-wider text-neutral-500">
                    {bankAccount.swiftBic}
                  </div>
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-lg p-1.5 text-primary-700 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  aria-label="Copy SWIFT / BIC"
                  onClick={() => copyValue('swiftBic', bankAccount.swiftBic)}
                >
                  {copiedField === 'swiftBic' ? (
                    <CorrectIcon size="18px" />
                  ) : (
                    <LinkCopyIcon size="18px" />
                  )}
                </button>
              </div>
            )}
          </div>

          {(bankAccount.country || bankAccount.currency) && (
            <div className="flex flex-wrap gap-2">
              {bankAccount.country && (
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {bankAccount.country}
                </span>
              )}
              {bankAccount.currency && (
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {bankAccount.currency}
                </span>
              )}
            </div>
          )}

          {bankAccount.lastUpdatedBy && (
            <div className="text-xs text-gray-500">
              Last updated by {bankAccount.lastUpdatedBy}
              {bankAccount.lastUpdatedAt ? ` on ${formatTimestamp(bankAccount.lastUpdatedAt)}` : ''}
            </div>
          )}
        </div>
      )}

      {isEditing && (
        <div className="flex flex-col gap-4">
          {formError && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-scarlet">{formError}</div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {FIELDS.map((field) => {
              const inputId = `bank-account-${landlordId}-${field.key}`;
              return (
                <div key={field.key}>
                  <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-gray-700">
                    {field.label}
                    {field.optional ? ' (optional)' : ''}
                  </label>
                  <input
                    id={inputId}
                    className={inputClass(!!fieldErrors[field.key])}
                    placeholder={field.placeholder}
                    value={form[field.key]}
                    aria-invalid={!!fieldErrors[field.key]}
                    aria-describedby={fieldErrors[field.key] ? `${inputId}-error` : undefined}
                    onChange={(event) => handleChange(field.key, event.target.value)}
                  />
                  {fieldErrors[field.key] && (
                    <div id={`${inputId}-error`} className="mt-1 text-xs text-scarlet">
                      {fieldErrors[field.key]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type={ButtonType.transparent}
              size={ButtonSize.medium}
              disabled={isSaving}
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.primary}
              size={ButtonSize.medium}
              disabled={isSaving}
              onClick={handleSave}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default BankAccountCard;
